import { createContext, useContext, useMemo, useRef } from "react";

/**
 * How the composer and a missing-route problem open the Thread default's own "Runs with" picker
 * (W-07, S12 decided (a)): the header's row registers itself here, per Thread view, so two tabs
 * showing Threads never reach each other's picker and nothing looks the row up in the page.
 */
export interface RunsWithOpener {
  /** The row says how to open it; the returned function takes that back. */
  readonly register: (open: () => void) => () => void;
  /** Opens the registered row's picker; nothing while no row is shown. */
  readonly open: () => void;
}

export const RunsWithOpenerContext = createContext<RunsWithOpener | null>(null);

/** One opener for a Thread view, given to its `RunsWithOpenerContext`. */
export function useRunsWithOpenerSlot(): RunsWithOpener {
  const slot = useRef<(() => void) | null>(null);
  return useMemo(
    () => ({
      register: (open: () => void) => {
        slot.current = open;
        return () => {
          if (slot.current === open) slot.current = null;
        };
      },
      open: () => {
        slot.current?.();
      },
    }),
    [],
  );
}

/** The Thread view's opener; `null` outside a Thread view. */
export function useRunsWithOpener(): RunsWithOpener | null {
  return useContext(RunsWithOpenerContext);
}
